import { useNavigate, useSearch } from "@tanstack/react-router";
import { DEFAULT_PAGE_SIZE } from "@cpg/common";

// Generic URL-persisted list state for every Phase-4 list page (merchants,
// invoices, refunds, ledgers, assets, tariffs, transactions,
// webhook-deliveries, fireblocks-events).
//
// No route in this app declares `validateSearch` yet (that happens per-page
// in Phase 4) — this hook is intentionally decoupled from any specific
// route id, so it reads/writes search params via `strict: false` /
// `useNavigate()` without a `from`. That means TypeScript can't check the
// search shape against a route's `validateSearch` output here; the casts
// below are deliberate and the contract is enforced by convention: whichever
// route calls `useTableState` MUST declare a `validateSearch` that returns
// at least `{ page, pageSize, sortBy, sortOrder }` plus its own filter keys,
// flattened into the same search object (not nested under `filters`).

export type SortOrder = "asc" | "desc";

export interface TableSearchParams<TFilters extends Record<string, unknown>> {
  page?: number;
  pageSize?: number;
  sortBy?: string | null;
  sortOrder?: SortOrder;
}

/** The full search-param shape a calling route's `validateSearch` should return. */
export type TableSearch<TFilters extends Record<string, unknown>> = TableSearchParams<TFilters> &
  Partial<TFilters>;

export interface TableState<TFilters extends Record<string, unknown>> {
  page: number;
  pageSize: number;
  sortBy: string | null;
  sortOrder: SortOrder;
  filters: TFilters;
  setPage(page: number): void;
  setPageSize(pageSize: number): void;
  /** Toggles asc/desc if columnKey === current sortBy, else sets it with a sensible default direction. */
  setSort(columnKey: string): void;
  /** Also resets page to 1 — a changed filter invalidates the current page. */
  setFilter<K extends keyof TFilters>(key: K, value: TFilters[K]): void;
  reset(): void;
}

export interface UseTableStateOptions<TFilters extends Record<string, unknown>> {
  /** Used both as the "no filter set" value and to know which search keys are filter keys. */
  defaultFilters: TFilters;
  defaultPageSize?: number;
  defaultSortBy?: string | null;
  /** Direction applied the first time a column is sorted (a click on a new column always starts here). */
  defaultSortOrder?: SortOrder;
}

const RESERVED_SEARCH_KEYS = new Set<string>(["page", "pageSize", "sortBy", "sortOrder"]);

export function useTableState<TFilters extends Record<string, unknown>>(
  options: UseTableStateOptions<TFilters>,
): TableState<TFilters> {
  const {
    defaultFilters,
    defaultPageSize = DEFAULT_PAGE_SIZE,
    defaultSortBy = null,
    defaultSortOrder = "asc",
  } = options;

  // `strict: false` because the calling route isn't known here — see file
  // header. Cast through `any` on the options object: `useSearch`'s
  // generics can't be satisfied without a concrete route id, which this
  // hook deliberately doesn't have.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const search = useSearch({ strict: false } as any) as TableSearch<TFilters>;
  const navigate = useNavigate();

  const page = search.page ?? 1;
  const pageSize = search.pageSize ?? defaultPageSize;
  const sortBy = search.sortBy ?? defaultSortBy;
  const sortOrder = search.sortOrder ?? defaultSortOrder;

  // Every page on this app declares `defaultFilters` as an empty object
  // (`{}`) — none of these filter fields has a real default VALUE, "unset"
  // just means absent from the URL. Building `filters` from
  // `Object.keys(defaultFilters)` alone therefore always produced an empty
  // set of keys, so this hook never actually read a filter value back out of
  // the URL: every filter `<select>`/`<input>` stayed pinned to its "no
  // filter" display state regardless of what was in the URL, and the object
  // spread into each page's API params/queryKey never changed, so filter
  // changes silently never reached the query (no refetch, no API call).
  // Fixed by also picking up whatever non-reserved keys are actually present
  // in the current search — mirrors apps/merchant/src/lib/table-state.ts's
  // (already-correct) `filterKeys` computation.
  const searchRecord = search as Record<string, unknown>;
  const filterKeys = new Set<string>([
    ...Object.keys(defaultFilters),
    ...Object.keys(searchRecord).filter((key) => !RESERVED_SEARCH_KEYS.has(key)),
  ]);

  const filters = Array.from(filterKeys).reduce<TFilters>((acc, key) => {
    const value = searchRecord[key];
    (acc as Record<string, unknown>)[key] = value === undefined ? defaultFilters[key as keyof TFilters] : value;
    return acc;
  }, {} as TFilters);

  // Untyped on purpose — see the `useSearch` comment above. The public
  // `TableState<TFilters>` surface returned by this hook stays fully typed;
  // only this internal bridge to the router needs to escape the type
  // system, because it merges partial patches into a search shape this
  // hook's generic can't fully resolve without a concrete route.
  function updateSearch(patch: Record<string, unknown>) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    void navigate({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      search: ((prev: any) => ({ ...prev, ...patch })) as any,
    } as any);
  }

  return {
    page,
    pageSize,
    sortBy,
    sortOrder,
    filters,
    setPage(nextPage) {
      updateSearch({ page: nextPage });
    },
    setPageSize(nextPageSize) {
      // Changing pageSize also resets page to 1 — the old page number is
      // meaningless against a new page size.
      updateSearch({ pageSize: nextPageSize, page: 1 });
    },
    setSort(columnKey) {
      if (sortBy === columnKey) {
        updateSearch({ sortBy: columnKey, sortOrder: sortOrder === "asc" ? "desc" : "asc" });
      } else {
        updateSearch({ sortBy: columnKey, sortOrder: defaultSortOrder });
      }
    },
    setFilter(key, value) {
      // A changed filter invalidates the current page, so reset to 1.
      updateSearch({ [key as string]: value, page: 1 });
    },
    reset() {
      // Same "actually-present keys" fix as `filters` above — clearing must
      // reach every filter key currently in the URL, not just the (always
      // empty) `defaultFilters` key set, or "Clear filters" would silently
      // leave every applied filter in place.
      const observedFilterKeys = Object.keys(searchRecord).filter((key) => !RESERVED_SEARCH_KEYS.has(key));
      const clearedFilters = Array.from(new Set([...Object.keys(defaultFilters), ...observedFilterKeys])).reduce<
        Record<string, unknown>
      >((acc, key) => {
        acc[key] = defaultFilters[key as keyof TFilters];
        return acc;
      }, {});

      updateSearch({
        page: 1,
        pageSize: defaultPageSize,
        sortBy: defaultSortBy,
        sortOrder: defaultSortOrder,
        ...clearedFilters,
      });
    },
  };
}
